import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    FaArrowLeft,
    FaEye,
    FaCalendarAlt,
    FaCheckCircle,
    FaTimesCircle,
    FaEdit,
    FaAd,
    FaList,
    FaLink,
    FaImage
} from 'react-icons/fa';
import { HiOutlineRefresh } from 'react-icons/hi';

import Loader from '../../components/Loader';
import NotFound from '../../components/UI/NotFound';
import { formatView } from '../../components/formatView';
import Calling from '../../api/Calling';

const BlogInfo = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loader, setLoader] = useState(false);
    const [blog, setBlog] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (id) {
            fetchBlogById();
        }
    }, [id]);

    const fetchBlogById = async () => {
        setLoader(true);
        setNotFound(false);
        try {
            const resp = await Calling.getBlogId(id);
            if (resp?.status && resp?.data) {
                setBlog(resp.data);
            } else {
                setNotFound(true);
                toast.error(resp?.message || 'Blog not found');
            }
        } catch (error) {
            console.error('Fetch blog error:', error);
            setNotFound(true);
            toast.error('Failed to fetch blog details');
        } finally {
            setLoader(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };


    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    if (loader) {
        return <Loader loader={loader} />;
    }

    if (notFound) {
        return (
            <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                <div className="mx-auto max-w-7xl">
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 px-4 py-2 mb-4 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <FaArrowLeft /> Back
                    </button>
                    <NotFound />
                </div>
            </div>
        );
    }

    if (!blog) {
        return null;
    }

    return (
        <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
            <div className="mx-auto max-w-7xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                    >
                        <FaArrowLeft /> Back
                    </button>
                    <div className="flex gap-2">
                        <button
                            onClick={fetchBlogById}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors shadow-sm"
                        >
                            <HiOutlineRefresh className="text-lg" />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Main Info Card */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 mb-6">
                    <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-main-blue/5 to-main-blue/10">
                        <h1 className="text-3xl font-bold text-gray-900 mb-3">{blog.main_title}</h1>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                                <FaCalendarAlt className="text-main-blue" />
                                <span>Created: {formatDate(blog.createdAt)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <FaCalendarAlt className="text-main-blue" />
                                <span>Updated: {formatDate(blog.updatedAt)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <FaEye className="text-main-blue" />
                                <span className="font-semibold text-main-blue">{formatView(blog.views)} views</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {blog.status ? (
                                    <>
                                        <FaCheckCircle className="text-green-500" />
                                        <span className="text-green-600 font-semibold">Active</span>
                                    </>
                                ) : (
                                    <>
                                        <FaTimesCircle className="text-red-500" />
                                        <span className="text-red-600 font-semibold">Inactive</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Blog Image */}
                    <div className="p-6 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                            <FaImage className="text-main-blue" />
                            Featured Image
                        </h2>
                        <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                            <img
                                src={blog.main_img}
                                alt={blog.main_title}
                                className="w-full h-auto object-cover max-h-[500px]"
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Found';
                                }}
                            />
                        </div>
                    </div>

                    {/* Main Description */}
                    <div className="p-6 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-gray-800 mb-3">Description</h2>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-justify">
                            {blog.main_description}
                        </p>
                    </div>

                    {/* Call to Action */}
                    {blog.cta && (
                        <div className="p-6 border-b border-gray-100 bg-blue-50/30">
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <FaLink className="text-main-blue" />
                                Call to Action
                            </h2>
                            <div className="flex items-center gap-2">
                                {isValidUrl(blog.cta) ? (
                                    <a
                                        href={blog.cta}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-main-blue text-white rounded-lg font-medium hover:bg-main-blue/80 transition-colors"
                                    >
                                        <FaLink />
                                        Visit Link
                                    </a>
                                ) : (
                                    <span className="px-4 py-2 bg-main-blue text-white rounded-lg font-medium">
                                        {blog.cta}
                                    </span>
                                )}
                            </div>
                            {isValidUrl(blog.cta) && (
                                <p className="text-xs text-gray-500 mt-2 break-all">
                                    {blog.cta}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Metadata */}
                    <div className="p-6 bg-gray-50/50">
                        <h2 className="text-lg font-semibold text-gray-800 mb-3">Metadata</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-500 mb-1">Blog ID</p>
                                <p className="font-mono text-xs text-gray-900 break-all">{blog._id}</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-500 mb-1">Total Views</p>
                                <p className="text-2xl font-bold text-main-blue">{formatView(blog.views)}</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-500 mb-1">Sub Sections</p>
                                <p className="text-2xl font-bold text-purple-600">
                                    {blog.sub_info?.length || 0}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sub Sections */}
                {blog.sub_info && blog.sub_info.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-blue-50">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                    <FaList className="text-main-blue text-xl" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-800">Sub Sections</h2>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        {blog.sub_info.length} section{blog.sub_info.length !== 1 ? 's' : ''} in this blog
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50/50 space-y-4">
                            {blog.sub_info.map((section, index) => (
                                <div
                                    key={index}
                                    className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200"
                                >
                                    <div className="p-5">
                                        {/* Section Header */}
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                    <span className="px-3 py-1 bg-gradient-to-r from-main-blue to-blue-600 text-white text-xs font-semibold rounded-full">
                                                        Section {index + 1}
                                                    </span>
                                                    {section.sub_ads ? (
                                                        <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                                            <FaAd className="text-xs" />
                                                            Ads Enabled
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                                                            <FaAd className="text-xs" />
                                                            Ads Disabled
                                                        </span>
                                                    )}
                                                </div>
                                                <h3 className="text-lg font-bold text-gray-900">
                                                    {section.sub_title || 'Untitled Section'}
                                                </h3>
                                            </div>
                                        </div>

                                        {/* Section Description */}
                                        {section.sub_desciption ? (
                                            <div className="mt-3 pt-3 border-t border-gray-100">
                                                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                                    {section.sub_desciption}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="mt-3 pt-3 border-t border-gray-100">
                                                <p className="text-sm text-gray-400 italic">
                                                    No description provided for this section
                                                </p>
                                            </div>
                                        )}

                                        {/* Section Footer */}
                                        <div className="mt-4 pt-3 border-t border-gray-100">
                                            <div className="flex items-center gap-2 text-sm">
                                                {section.sub_ads ? (
                                                    <span className="px-3 py-1 bg-green-50 text-green-700 rounded-md font-medium">
                                                        💰 Monetized Section
                                                    </span>
                                                ) : (
                                                    <span className="px-3 py-1 bg-gray-50 text-gray-600 rounded-md font-medium">
                                                        🚫 Non-monetized Section
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Empty State for Sub Sections */}
                {(!blog.sub_info || blog.sub_info.length === 0) && (
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 p-12">
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                                <FaList className="text-gray-400 text-3xl" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                No Sub Sections
                            </h3>
                            <p className="text-gray-500 text-sm max-w-md mx-auto">
                                This blog doesn't have any sub sections yet. Sub sections help organize content into smaller, manageable parts.
                            </p>

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogInfo;